import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useMotionValue } from "framer-motion";
import { useContext, useLayoutEffect, useRef } from "react";
import { FaAndroid, FaApple } from "react-icons/fa";
import { ConfigContext } from "../../../../config/configContext";
import Button from "../../../atoms/button/Button";
import SingleScreenshot from "./singleScreenshot";

const heroSpanSrc = `${import.meta.env.BASE_URL}entry-page/hero-section/hero-span.svg`;

gsap.registerPlugin(ScrollTrigger);

const storeIconClass = "h-5 w-5 shrink-0";

function Header() {
  const {
    googlePlayLink,
    appStoreLink,
    home: { header },
  } = useContext(ConfigContext)!;

  const triggerRef = useRef<HTMLDivElement>(null);
  const scrollProgress = useMotionValue(0);

  useLayoutEffect(() => {
    const el = triggerRef.current;
    const count = header.screenshots.length;
    if (!el || count === 0) return;

    const scrollDistance = () =>
      Math.max(window.innerHeight, 480) * Math.max(count, 1);

    const st = ScrollTrigger.create({
      trigger: el,
      start: "top 80px",
      end: () => `+=${scrollDistance()}`,
      pin: true,
      pinSpacing: true,
      scrub: 0.6,
      onUpdate: (self) => {
        scrollProgress.set(self.progress);
      },
    });

    const onResize = () => ScrollTrigger.refresh();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      st.kill();
      scrollProgress.set(0);
    };
  }, [header.screenshots.length]);

  return (
    <div ref={triggerRef} className="relative w-full">
      <div className="w-full px-4">
        <div className="flex flex-col md:flex-row md:items-stretch">
          
          
          <div className="flex min-h-0 flex-1 items-center md:items-start">
            <div className="static top-40 flex min-h-0 flex-col justify-center py-8 prose md:min-h-[548px]">
              <h2 className="mb-4 mt-0 text-4xl md:text-6xl">
                {header.headlineMark ? (
                  <>
                    {header.headline
                      .split(" ")
                      .slice(0, header.headlineMark[0])
                      .join(" ")}{" "}
                    <span className="inline-block rounded-lg bg-primary px-2 py-0.5 !text-white md:px-3 md:py-1">
                      {header.headline
                        .split(" ")
                        .slice(...header.headlineMark)
                        .join(" ")}
                    </span>{" "}
                    {header.headline
                      .split(" ")
                      .slice(header.headlineMark[1])
                      .join(" ")}
                  </>
                ) : (
                  header.headline
                )}
              </h2>
              <p className="m-0 my-1 max-w-md whitespace-pre-wrap text-left opacity-70 md:max-w-lg md:text-lg">
                {header.subtitle}
              </p>
              {header.usersDescription && (
                <div className="not-prose my-1 flex items-center gap-2">
                  <ul className="avatar-group -space-x-4">
                    {Array.from(Array(5)).map((_, index) => (
                      <li key={index} className="avatar">
                        <div className="w-8">
                          <img
                            src={`/avatars/${index + 1}.webp`}
                            alt={`app user ${index + 1}`}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm">{header.usersDescription}</p>
                </div>
              )}
              <div className="not-prose mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <Button
                  type="button"
                  variant="validation"
                  expandWidth
                  className="!mt-0"
                  disabled={!googlePlayLink?.trim()}
                  onClick={() => {
                    const url = googlePlayLink?.trim();
                    if (url) window.open(url, "_blank", "noopener,noreferrer");
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    <FaAndroid className={storeIconClass} aria-hidden />
                    Telecharger Android
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="cancel"
                  expandWidth
                  className="!mt-0"
                  disabled={!appStoreLink?.trim()}
                  onClick={() => {
                    const url = appStoreLink?.trim();
                    if (url) window.open(url, "_blank", "noopener,noreferrer");
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    <FaApple className={storeIconClass} aria-hidden />
                    Telecharger IOS
                  </span>
                </Button>
              </div>
            </div>
          </div>


          <div className="z-0 flex w-full shrink-0 justify-center md:w-auto md:max-w-[min(100%,560px)]">
            <div className="relative isolate inline-flex justify-center self-start overflow-visible">
              <img
                src={heroSpanSrc}
                alt=""
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 z-0 hidden h-auto w-[600px] max-w-none -translate-x-1/2 -translate-y-1/2 select-none opacity-60 xl:block"
              />
              <div className="relative z-10 h-[548px] rounded-[3rem] sm:h-[648px] md:h-[548px] 2xs:h-[720px]">
                <div className="absolute top-2.5 left-3 w-[calc(100%-24px)] h-[calc(100%-16px)] rounded-[1rem] 2xs:rounded-[2rem] overflow-hidden">
                  {header.screenshots.map((src, index) => (
                    <SingleScreenshot
                      key={src}
                      src={src}
                      scrollYProgress={scrollProgress}
                      index={index}
                      totalCount={header.screenshots.length}
                    />
                  ))}
                </div>
                <img
                  src="/misc/iphone-frame.webp"
                  alt="iphone-frame"
                  className="relative z-10 h-full"
                />
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}

export default Header;
